import React, { useEffect, useState } from 'react';
import { ReactComponent as CopyIcon } from 'assets/icons/copy.svg';
import { ReactComponent as CheckIcon } from 'assets/icons/check.svg';
import config from 'config/constants';
import FormRequestBlock from 'components/FormRequestBlock';
import { Button } from 'components/ui/button';
import { Textarea } from 'components/ui/textarea';
import { Typography } from 'components/ui/typography';
import { useAppDispatch, useAppSelector } from 'app/store';
import {
  selectUserInfo,
  selectUserJwtValidationKeyLoading,
  updateJwtValidationKey,
} from 'app/store/user';
import { trackSegmentEvent } from 'app/utils';
import { ACTION, SOURCE } from 'app/analytics';
import './styles.scss';

const JWTValidationKeyBlock = () => {
  const { jwtValidationKey, uid, isReadOnly } = useAppSelector(selectUserInfo) || {};
  const formLoading = useAppSelector(selectUserJwtValidationKeyLoading);

  const [inputValue, setInputValue] = useState(jwtValidationKey ?? '');
  const [justCopied, setJustCopied] = useState<boolean>(false);

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (jwtValidationKey) {
      setInputValue(jwtValidationKey);
    }
  }, [jwtValidationKey]);

  const handleSubmit = (e: React.SyntheticEvent) => {
    e.preventDefault();
    if (isReadOnly) {
      return;
    }
    dispatch(updateJwtValidationKey(inputValue));
    trackSegmentEvent(SOURCE.JWT_MODULE, ACTION.CLICK, 'update_button');
  };

  function handleCopyClick(e: React.SyntheticEvent) {
    e.preventDefault();

    if (uid) {
      navigator.clipboard.writeText(uid);

      // Change the copy icon to a checkmark for ~ 1s
      setJustCopied(true);
      setTimeout(() => setJustCopied(false), 1000);
    }
  }

  return (
    <FormRequestBlock
      styles={{ form: { flexDirection: 'column', gap: '10px' } }}
      title="Add a JWT validation key for your account"
      onSubmit={handleSubmit}
      loading={formLoading}
      disabled={isReadOnly}
      text={
        <>
          <p>
            <Typography variant="link" asChild>
              <a href={config.external.doc.jwtDocs} target="_blank" rel="noreferrer">
                Follow this guide to learn how to use your JWT key
              </a>
            </Typography>
          </p>

          <div className="copyable-secret">
            <p>Your unique JWT issuer ID: </p>

            <div className="copyable-secret__value" onClick={handleCopyClick}>
              {uid}{' '}
              {justCopied ? (
                <CheckIcon height={16} className="copyable-secret__icon" />
              ) : (
                <CopyIcon height={16} className="copyable-secret__icon" />
              )}
            </div>
          </div>
        </>
      }
    >
      <Textarea
        placeholder="Enter a JWT validation key"
        rows={9}
        required
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        disabled={isReadOnly}
      />
      <div className="flex flex-row justify-end">
        <Button type="submit" variant="secondary" disabled={formLoading || isReadOnly}>
          Save
        </Button>
      </div>
    </FormRequestBlock>
  );
};

export default JWTValidationKeyBlock;
